import {
  Avatar,
  Box,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Tooltip,
  Typography
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type React from 'react'
import { type AttendanceAccountContext, type AttendanceFilterOption, type AttendanceMetric, type AttendanceStatusTone } from 'Pages/Attendance/types'

const semanticToneStyles: Record<Exclude<AttendanceStatusTone, 'neutral'>, { background: string, color: string, border: string }> = {
  success: {
    background: '#EAF6EF',
    color: '#1C6B3C',
    border: '#BFDCC9'
  },
  warning: {
    background: '#FFF7E6',
    color: '#7A4D00',
    border: '#F2D28A'
  },
  error: {
    background: '#FDECEC',
    color: '#9B1C1C',
    border: '#F2B6B6'
  }
}

export function AttendanceStatusIndicator (props: { label: string, tone?: AttendanceStatusTone }): JSX.Element {
  const tone = props.tone ?? 'neutral'

  return (
    <Chip
      label={props.label}
      size="small"
      sx={{
        backgroundColor: theme => tone === 'neutral' ? theme.palette.action.hover : semanticToneStyles[tone].background,
        border: theme => `1px solid ${tone === 'neutral' ? theme.palette.divider : semanticToneStyles[tone].border}`,
        borderRadius: '6px',
        color: theme => tone === 'neutral' ? theme.palette.text.secondary : semanticToneStyles[tone].color,
        fontWeight: 600
      }}
    />
  )
}

export function AttendancePageShell (props: {
  title: string
  titleSuffix?: string
  subtitle?: string
  accountContext: AttendanceAccountContext
  children: React.ReactNode
  actions?: React.ReactNode
  eyebrow?: React.ReactNode
  toolbar?: React.ReactNode
}): JSX.Element {
  return (
    <Box sx={{ backgroundColor: theme => theme.palette.grey[50], minHeight: 'calc(100vh - 130px)' }}>
      <Box sx={{ alignItems: { xs: 'stretch', md: 'flex-start' }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2.5, mb: 2.5 }}>
        <Box>
          {props.eyebrow !== undefined && (
            <Box sx={{ mb: 1 }}>
              {props.eyebrow}
            </Box>
          )}
          <Typography variant="h3">
            {props.title}
            {props.titleSuffix !== undefined && (
              <Typography component="span" variant="h4" sx={{ color: 'text.secondary', ml: 1 }}>
                ({props.titleSuffix})
              </Typography>
            )}
          </Typography>
          {props.subtitle !== undefined && (
            <Typography sx={{ color: 'text.secondary', mt: 1 }}>
              {props.subtitle}
            </Typography>
          )}
        </Box>
        {props.actions !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, pt: { xs: 0, md: 0.5 } }}>
            {props.actions}
          </Box>
        )}
      </Box>
      {props.toolbar !== undefined && (
        <Box sx={{ mb: 2.5 }}>
          {props.toolbar}
        </Box>
      )}
      {props.children}
    </Box>
  )
}

export function AttendanceFilterBar (props: { filters?: AttendanceFilterOption[], children?: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ alignItems: 'flex-start', display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: '21px' } }}>
      {(props.filters ?? []).map(filter => (
        <Box key={`${filter.label}-${filter.value}`} sx={{ minWidth: 154 }}>
          <Typography sx={{ color: '#8F8F8F', fontSize: '0.75rem', fontWeight: 500 }}>
            {filter.label}
          </Typography>
          <Typography sx={{ color: '#29262E', fontSize: '0.875rem', fontWeight: 700 }}>
            {filter.value}
          </Typography>
        </Box>
      ))}
      {props.children}
    </Box>
  )
}

export function AttendanceFilterSelect (props: {
  id: string
  label: string
  value: string
  options: Array<{ value: string, label: string }>
  onChange: (value: string) => void
}): JSX.Element {
  return (
    <FormControl size="small" sx={{ width: { xs: '100%', sm: 154 } }}>
      <Typography
        component="label"
        id={`${props.id}-label`}
        htmlFor={props.id}
        sx={{ color: '#8F8F8F', fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.25 }}
      >
        {props.label}
      </Typography>
      <Select
        labelId={`${props.id}-label`}
        id={props.id}
        value={props.value}
        inputProps={{ 'aria-label': props.label }}
        onChange={(event: SelectChangeEvent) => { props.onChange(event.target.value) }}
        sx={{
          color: '#29262E',
          fontSize: '0.875rem',
          fontWeight: 700,
          height: 32,
          '& .MuiSelect-select': {
            alignItems: 'center',
            display: 'flex',
            minHeight: '0 !important',
            overflow: 'hidden',
            pl: 0,
            pr: '28px !important',
            py: 0.5,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 0
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 0
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: theme => `1px solid ${theme.palette.primary.main}`
          },
          '& .MuiSvgIcon-root': {
            color: '#0F0F10',
            right: 0
          }
        }}
      >
        {props.options.map(option => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export function AttendanceSessionCard (props: {
  name: string
  facility: string
  staff: string
  schedule: string
  dateRange?: string
  dateRangeNote?: string
  days?: Array<{ label: string, active: boolean }>
  time?: string
  statusLabel: string
  statusTone: AttendanceStatusTone
  statusTooltip?: string
  metrics: AttendanceMetric[]
  disabled?: boolean
  onAction?: () => void
}): JSX.Element {
  const actionable = props.onAction !== undefined
  const disabled = props.disabled === true
  const statusIcon = props.statusTone === 'success'
    ? <CheckCircleIcon aria-hidden="true" sx={{ color: theme => theme.palette.neutral?.main ?? '#34AFB8', fontSize: 20 }} />
    : props.statusTone === 'neutral'
      ? <InfoOutlinedIcon aria-hidden="true" sx={{ color: 'text.secondary', fontSize: 20 }} />
      : <ErrorOutlineIcon aria-hidden="true" sx={{ color: '#7A4D00', fontSize: 20 }} />

  return (
    <Paper
      component={actionable ? 'button' : 'article'}
      type={actionable ? 'button' : undefined}
      aria-disabled={actionable && disabled ? true : undefined}
      onClick={disabled ? undefined : props.onAction}
      data-testid="attendance-session-card"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid rgba(89, 89, 89, 0.3)',
        borderRadius: '12px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
        color: 'inherit',
        cursor: !actionable ? 'default' : disabled ? 'wait' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '17px',
        minHeight: 199,
        overflow: 'hidden',
        p: '20px',
        textAlign: 'left',
        width: '100%',
        '&:focus-visible': {
          outline: theme => `3px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px'
        },
        '&:hover': !actionable || disabled
          ? undefined
          : {
              borderColor: theme => theme.palette.primary.main,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)'
            }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
        <Typography variant="h6" sx={{ color: '#222024', fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 }}>
          {props.name}
        </Typography>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
          <Avatar sx={{ bgcolor: '#F1EBF9', color: '#7136BC', flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, height: 30, width: 30 }}>
            {props.staff.trim().charAt(0).toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#29262E', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {props.staff}
            </Typography>
            <Typography sx={{ color: '#514F53', fontSize: '0.75rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {props.facility}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ color: 'text.primary', display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {props.dateRange !== undefined && (
          <Typography sx={{ color: '#29262E', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.3 }}>{props.dateRange}</Typography>
        )}
        {props.dateRangeNote !== undefined && (
          <Typography sx={{ color: '#514F53', fontSize: '0.75rem', lineHeight: 1.3 }}>{props.dateRangeNote}</Typography>
        )}
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {props.days !== undefined && (
            <Box aria-label={`Scheduled days: ${props.schedule}`} role="img" sx={{ display: 'flex', gap: '4px' }}>
              {props.days.map(day => (
                <Box
                  key={day.label}
                  aria-hidden="true"
                  sx={{
                    alignItems: 'center',
                    aspectRatio: '1 / 1',
                    backgroundColor: day.active ? '#7136BC' : 'rgba(74, 74, 74, 0.1)',
                    borderRadius: '50%',
                    color: day.active ? '#F1EBF9' : '#4A4A4A',
                    display: 'flex',
                    flex: '0 0 22px',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    height: 22,
                    justifyContent: 'center',
                    lineHeight: 1,
                    minHeight: 22,
                    minWidth: 22,
                    p: 0,
                    width: 22
                  }}
                >
                  {day.label}
                </Box>
              ))}
            </Box>
          )}
          <Typography sx={{ color: '#514F53', fontSize: '0.75rem', lineHeight: 1.3 }}>{props.time ?? props.schedule}</Typography>
        </Box>
      </Box>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, justifyContent: 'space-between', mt: 'auto', width: '100%' }}>
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
          {props.metrics.map(metric => (
            <Box key={metric.label} sx={{ display: 'flex', gap: '5px' }}>
              {metric.tooltip === undefined
                ? (
                  <Typography sx={{ color: '#29262E', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{metric.value}</Typography>
                  )
                : (
                  <Tooltip title={metric.tooltip}>
                    <Typography
                      tabIndex={0}
                      sx={{
                        color: '#29262E',
                        cursor: 'help',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        lineHeight: 1.3,
                        textDecoration: 'underline dotted',
                        textUnderlineOffset: '3px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {metric.value}
                    </Typography>
                  </Tooltip>
                  )}
              <Typography sx={{ color: '#514F53', fontSize: '0.75rem', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{metric.label}</Typography>
            </Box>
          ))}
        </Box>
        <Tooltip
          title={props.statusTooltip ?? ''}
          describeChild
          disableHoverListener={props.statusTooltip === undefined}
          disableFocusListener={props.statusTooltip === undefined}
        >
          <Box tabIndex={props.statusTooltip === undefined ? undefined : 0} sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: '6px' }}>
            <Typography sx={{ color: '#0F0F10', fontSize: '0.625rem', whiteSpace: 'nowrap' }}>{props.statusLabel}</Typography>
            {statusIcon}
          </Box>
        </Tooltip>
      </Box>
    </Paper>
  )
}
